export interface UserTutorialStatus {
    allSeen?: boolean;
    introductionSeen?: boolean;
    invitingSeen?: boolean;
    schedulingSeen?: boolean;
    dischargingSeen?: boolean;
    oldUserSeen?: boolean;
}

export enum UserTutorialStatusType {
    ALL = 'ALL',
    INTRODUCTION = 'INTRODUCTION',
    INVITING = 'INVITING',
    SCHEDULING = 'SCHEDULING',
    DISCHARGING = 'DISCHARGING',
    OLD_USER = 'OLD_USER',
}

export interface TutorialAction {
    text: string; // The text that shows on the button
    actionName: SlideAction; // The function that will be called when the button is clicked
    class?: string[]; // An array of classes to add to the button
    color?: 'primary' | 'accent' | 'warn' | 'secondary'; // The color of the mat button
    type?: 'button' | 'link';
    prefixText?: string;
    suffixText?: string;
}

export enum SlideAction {
    SHOW_NEXT_SLIDE = 'SHOW_NEXT_SLIDE',
    CLOSE_TUTORIAL = 'CLOSE_TUTORIAL',
    START_NETWORK_TUTORIAL = 'START_NETWORK_TUTORIAL',
    START_CODING_TUTORIAL = 'START_CODING_TUTORIAL',
    START_SCHEDULE_TUTORIAL = 'START_SCHEDULE_TUTORIAL',
    START_NOTE_TUTORIAL = 'START_NOTE_TUTORIAL',
    START_CONSENT_TUTORIAL = 'START_CONSENT_TUTORIAL',
    START_SCHEDULE_SIDE_NAV_TUTORIAL = 'START_SCHEDULE_SIDE_NAV_TUTORIAL',
    START_PATIENT_SIDE_NAV_TUTORIAL = 'START_PATIENT_SIDE_NAV_TUTORIAL',
    START_SUPPORT_STAFF_TUTORIAL = 'START_SUPPORT_STAFF_TUTORIAL',
    SHOW_CLOSING_SLIDE = 'SHOW_CLOSING_SLIDE',
    NAVIGATE_SCHEDULE = 'NAVIGATE_SCHEDULE',
    SHOW_PREV_SLIDE = 'SHOW_PREV_SLIDE',
    OLD_LOGIN_CONFIRM_NOW = 'CONFIRM_NOW',
    OLD_LOGIN_CONFIRM_NEVER = 'OLD_LOGIN_CONFIRM_NEVER',
    SHOW_NOW = 'SHOW_NOW',
    START_DOCTOR_FORCE_SCHEDULE_SIDE_NAV_TUTORIAL = 'START_DOCTOR_FORCE_SCHEDULE_SIDE_NAV_TUTORIAL',
    CLOSE_GETTING_STARTED = 'CLOSE_GETTING_STARTED',
}

export interface TutorialMessage {
    text: string; // The actual text of the message block
    class?: string; // A class name for this message block to allow highlighting
    highlightTarget?: string; // A name for highlighting a button/target for action
    highlightColor?: string; // A color name
}

export interface TutorialPosition {
    x?: number; //actual x value coordinate
    y?: number; //actual y value coordinate
    vertical?: 'top' | 'center' | 'bottom';
    horizontal?: 'left' | 'center' | 'right';
    parentId?: string;
    parentClass?: string;
}

export interface TutorialSlide {
    key: string; // optional key fo the card
    title?: string; // Optional title for the slide
    messages: TutorialMessage[]; // The main message for the slide
    highlightTarget?: string; // A name for highlighting a button/target for action
    expansionTarget?: string; // A CSS class name or element ID to expand a section programmatically
    forceOpenTarget?: boolean,// This is used to force open a dialog if neeed
    keepDialogOpen?: boolean,// This is used to make sure the dialog stays open when navigating to this slide
    images?: string[]; // An array of images that can be used as slides
    video?: string[]; // an array of videos that can be used as slides
    actions?: TutorialAction[]; // An array of actions to display on the slide
    position?: TutorialPosition;
    hasBackdrop?: boolean;
    dimensions?: { width?: string; height?: string };
    hideClose?: boolean;
    hideConditions?: SlideHideCondition[];
    progressStart?: number;
    progressFinish?: number;
    progressBar?: number; // boolean, to mark the slide to show the progress bar
    hideDoNotShowAgain?: boolean;
    onShow?: string;
    onHide?: string;
    previousButton?: string,
    videoUrl?: string;
    thumbnailUrl?: string;
    tutorialStatusType?: UserTutorialStatusType,
    actionsClass?: string
}

export interface SlideHideCondition {
    condition: SlideHidePageCondition;
    type?: SlideHideConditionType;
}

export enum SlideHideConditionType {
    OR = 'OR',
    AND = 'AND',
}

export enum SlideHidePageCondition {
    NONE = 'NONE',
    IS_PHYSICIAN = 'IS_PHYSICIAN',
    NOT_PHYSICIAN = 'NOT_PHYSICIAN',
    NOT_PHYSICIAN_ROLE = 'NOT_PHYSICIAN_ROLE',
    NOT_ADMIN_USER = 'NOT_ADMIN_USER',
    SIGNATURE_SET = 'SIGNATURE_SET',
    PRACTICE_PROFILE_COMPLETE = 'PRACTICE_PROFILE_COMPLETE',
    NETWORK_COMPLETE = 'NETWORK_COMPLETE',
    HAS_HOSPITALS = 'HAS_HOSPITALS',
    LETTERHEAD_SET = 'LETTERHEAD_SET',
    MEDICATION_BUNDLES_CREATED = 'MEDICATION_BUNDLES_CREATED',
    PREFERRED_MEDICATION_SET = 'PREFERRED_MEDICATION_SET',
    IS_STAFF = 'IS_STAFF',
    NOTE_SETTINGS_COMPLETE = 'NOTE_SETTINGS_COMPLETE',
    AI_NOTE_DISABLED = 'AI_NOTE_DISABLED',
    SCHEDULE_COMPLETED = 'SCHEDULE_COMPLETED',
    APPOINTMENT_EDITED = 'APPOINTMENT_EDITED',
    DEFAULT_USER_CONSENT_FORM = 'DEFAULT_CONSENT_USER_FORM_EDITED',
    CUSTOM_CONSENT_FORM = 'CUSTOM_CONSENT_FORM_CREATED',
    COLLEAGUE_ADDED = 'COLLEAGUE_ADDED',
    CODING_BUNDLES_ADDED = 'CODING_BUNDLES_ADDED',
    AUTO_WARD_BUNDLES_ADDED = 'AUTO_WARD_BUNDLES_ADDED',
    COMMON_MODIFIER_ADDED = 'COMMON_MODIFIER_ADDED',
    CODING_FINISHED = 'CODING_FINISHED',
    DEFAULT_NOTES_ADDED = 'DEFAULT_NOTES_ADDED',
    CUSTOM_NOTES_ADDED = 'CUSTOM_NOTES_ADDED',
    NOTES_FINISHED = 'NOTES_FINISHED',
    DIALOG_SLIDE_HIDDEN = 'DIALOG_SLIDE_HIDDEN',
    INVITING_SLIDE_SEEN = 'INVITING_SLIDE_SEEN',
    SCHEDULE_SLIDES_SEEN = 'SCHEDULE_SLIDES_SEEN',
    ADMITTING_SLIDE_SEEN = 'ADMITTING_SLIDE_SEEN',
    USER_SEEN_ALL_VIDEOS = 'USER_SEEN_ALL_VIDEOS',
    USER_FIRST_LOGIN = 'USER_FIRST_LOGIN',
    IS_PHYSICIAN_ONLY = 'IS_PHYSICIAN_ONLY',
    HIDE_IF_PHYSICIAN_ONLY = 'HIDE_IF_PHYSICIAN_ONLY',
    IS_ALLIED_SPECIALTY = 'IS_ALLIED_SPECIALTY',
}

export enum TutorialSection {
    PROFILE_SETTINGS = 'PROFILE_SETTINGS',
    NOTE_SETTINGS = 'NOTE_SETTINGS',
    SCHEDULE_SETTINGS = 'SCHEDULE_SETTINGS',
    CONSENT_SETTINGS = 'CONSENT_SETTINGS',
    CODING_SETTINGS = 'CODING_SETTINGS',
    NETWORK_SETTINGS = 'NETWORK_SETTINGS',
    SUPPORT_STAFF = 'SUPPORT_STAFF',
    DIALOG_SLIDE = 'DIALOG_SLIDE',
    PATIENT_NAV = 'PATIENT_NAV',
    SCHEDULE_NAV = 'SCHEDULE_NAV',
    CLOSING_SLIDE = 'CLOSING_SLIDE',
    GETTING_STARTED = 'GETTING_STARTED',
    DOCTOR_OPTIONS_SLIDE = 'DOCTER_OPTION_SLIDES',
    OLD_USER_LOGIN = 'OLD_USER_LOGIN',
    DOCTOR_FORCE_SCHEDULE_NAV_TUTORIAL = 'DOCTOR_FORCE_SCHEDULE_NAV_TUTORIAL',
}